/* Partnerek modul — lista + route-olt részletpanel (F1).
 *
 * Forrás: `raw/sessions/2026-09-10-partnerek-modul-tervezes.md` (a 9 tulajdonosi döntés)
 * + `…-partnerek-agent-anyagok/ux-partner-design.md` (lista-verziók, panel-anatómia).
 *
 * A modell, amit a felület KÉPVISEL: a partner a bizonylat MÁSIK fele — bejövőn szállító,
 * kimenőn vevő (K2). Ezért nincs „típus" OSZLOP a tárolt adatból: a szerep a bizonylatokból
 * jön, és egy partner EGYSZERRE lehet szállító és vevő.
 *
 * ⚠ Ami szándékosan NINCS itt:
 *  - „Megerősítve" jelölés (K9): az emberi megerősítés mint fogalom megszűnt, a névre
 *    azonosítás magától fut, a döntés provenienciája a bizonylat-soron látszik.
 *  - „Adó és Járulék" / „Munkavállaló" típus-pill: ezeknek MA nincs partner-soruk
 *    (`partner_identity.attach_document` visszafordul rájuk) — ami nincs, azt ne mutassuk
 *    letiltva. Ez az F3 munkája.
 *  - kézi bankszámla-bevitel (K8): a számlaszám kizárólag megfigyelésből kerülhet a törzsbe.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { X, Users } from 'lucide-react';
import {
  Amount, Badge, Card, ListCard, Pill, QueryState, SearchField,
  SlideOver, Table, Td,
} from '../components/UI';
import { Pagination } from '../components/Pagination';
import { partnersApi } from '../services/api';
import { fmtIban } from '../utils';

const PAGE_SIZE = 100;

/* A négy típus (F3/B7, 2026-09-16). A szerep a bizonylatokból jön, KIVÉVE a két
 * saját-kulcsos típust: egy adónem nem „szállító", és egy munkavállaló sem az.
 *
 * ⚠ A „Munkavállaló" SZÁNDÉKOSAN nincs benne a „Mind" nézetben (Gábor döntése): a név és a
 * bérösszeg együtt személyes adat, ezért csak külön kattintásra jön elő. Ez nem rejtés —
 * a pill ott van, és a lista fejléce kiírja, hogy a Mind nézet nem tartalmazza. */
const ROLE_FILTERS = [
  { key: 'all',       label: 'Mind' },
  { key: 'supplier',  label: 'Szállító' },
  { key: 'customer',  label: 'Vevő' },
  { key: 'authority', label: 'Adó és Járulék' },
  { key: 'employee',  label: 'Munkavállaló' },
];

// K5 (terminológia): egy fogalom = egy szó. „Számlázó" / „Beszállító" / „Kiállító" nem.
// A Bizonylatok oldalon a B.A.J. rövidítés marad — ott a felhasználó már megtanulta —,
// de itt kiírva szerepel (az UX-terv 7.3 kikötése).
const ROLE_LABEL = { supplier: 'Szállító', customer: 'Vevő',
                     authority: 'Adó és Járulék', employee: 'Munkavállaló' };
const ROLE_BADGE = { supplier: 'incoming', customer: 'outgoing',
                     authority: 'warning', employee: 'primary' };

// K9-proveniencia: mi alapján került a bizonylat a partnerhez. A `name` az egyetlen,
// ami emberi figyelmet érdemelhet — ezért az kap színt, a többi semleges.
const MATCH_LABEL = {
  tax_root:    { text: 'adószám',     variant: 'default' },
  foreign_vat: { text: 'külföldi VAT', variant: 'default' },
  iban:        { text: 'IBAN',        variant: 'default' },
  name:        { text: 'név alapján', variant: 'warning' },
};

const fmtDate = (v) => (v ? String(v).slice(0, 10).replace(/-/g, '.') + '.' : '–');

/* A partner látható AZONOSÍTÓJA. ⚠ MÉRVE (2026-09-16, dev DB = prod-dump): a 304 partner
 * MINDEGYIKÉN `tax_number IS NULL` — a 2026-09-06-i backfill (`build_partner_master.py::apply`)
 * `tax_number=None`-t ír, és CSAK a `tax_root`-ot (a 8 jegyű törzsszámot) tölti. A teljes
 * adószám a bizonylatokon ott van (`documents.supplier_tax_number`: 1310/1370), a törzsbe
 * nem került át. Amíg nem kerül át, a törzsszámot mutatjuk — ÜRES CELLA HELYETT. */
const partnerIdent = (p) => p.tax_number
  || (p.tax_root ? `HU ${p.tax_root}` : null)
  || (p.foreign_vat_number ? `${p.country_code || ''} ${p.foreign_vat_number}`.trim() : null);

export default function Partners() {
  const nav = useNavigate();
  const { pid } = useParams();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(1);

  const query = useQuery({ queryKey: ['partners'], queryFn: partnersApi.list });
  const all = query.data || [];

  const filtered = useMemo(() => {
    const f = q.trim().toLowerCase();
    // ⚠ A keresésben a SZÓKÖZ NÉLKÜLI alakot is nézzük: a felület négyes csoportokban
    // mutatja az IBAN-t és az adószámot is kötőjelezve, tehát a felhasználó szinte biztosan
    // a MEGJELENÍTETT (tagolt) alakot másolja be — az pedig sosem egyezne a tárolttal.
    const fc = f.replace(/[\s-]/g, '');
    const match = (p) => !f
      || (p.name || '').toLowerCase().includes(f)
      || (p.tax_number || '').toLowerCase().replace(/[\s-]/g, '').includes(fc)
      || (p.tax_root || '').includes(fc)
      || (p.foreign_vat_number || '').toLowerCase().includes(fc)
      || (p.address || '').toLowerCase().includes(f)
      || (p.bank_accounts || []).some(a => (a.iban || '').toLowerCase().includes(fc));
    // ⚠ A „Mind" nézet a munkavállalókat NEM tartalmazza (személyes adat) — csak a saját
    // pilljükön jönnek elő. Minden más típus benne van.
    const inRole = (p) => role === 'all'
      ? !(p.roles || []).includes('employee')
      : (p.roles || []).includes(role);

    const val = (p) => {
      const c = p.doc_counts || {};
      switch (sort.key) {
        case 'total':     return c.total ?? 0;
        case 'total_year':return c.total_year ?? 0;
        case 'last':      return c.last_document || '';
        default:          return (p.name || '').toLowerCase();
      }
    };
    return all.filter(p => match(p) && inRole(p)).sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'desc' ? -cmp : cmp;
    });
  }, [all, q, role, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Szövegnél növekvő, számnál/dátumnál csökkenő az első kattintás iránya — a
  // „legtöbb bizonylat" és a „legutóbbi" az érdekes vég, nem a nulla.
  const handleSort = (key) => setSort(s => s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' ? 'asc' : 'desc' });

  const columns = [
    { key: 'name',       label: 'Partner',   sortable: true },
    { key: 'roles',      label: 'Szerep',    width: 150 },
    { key: 'total',      label: 'Bizonylat', sortable: true, align: 'right', width: 90,
      title: 'Az összes partnerhez kötött bizonylat' },
    { key: 'total_year', label: `${new Date().getFullYear()}`, sortable: true, align: 'right', width: 80,
      title: 'Ebben a naptári évben kelt bizonylatok' },
    { key: 'last',       label: 'Utolsó',    sortable: true, align: 'right', width: 100 },
  ];

  const roleBadges = (p) => (p.roles || []).map(r => (
    <Badge key={r} variant={ROLE_BADGE[r] || 'default'}>{ROLE_LABEL[r] || r}</Badge>
  ));

  const openPartner = (p) => nav(`/partners/${p.id}`);

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        gap:12,flexWrap:'wrap',marginBottom:'0.9rem'}}>
        <h1 style={{fontSize:'1.15rem',fontWeight:700,display:'flex',alignItems:'center',gap:8}}>
          <Users size={18} style={{color:'var(--text-muted)'}}/> Partnerek
        </h1>
        <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>
          {filtered.length === all.length
            ? `${all.length} partner`
            : `${filtered.length} / ${all.length} partner`}
        </span>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:'0.8rem'}}>
        <SearchField value={q} onChange={(v)=>{ setQ(v); setPage(1); }} onClear={()=>setQ('')}
          placeholder="Név, adószám, cím vagy számlaszám…" style={{minWidth:260,flex:'1 1 260px'}}/>
        <div style={{display:'flex',gap:6}}>
          {ROLE_FILTERS.map(f => (
            <Pill key={f.key} size="lg" active={role === f.key}
              onClick={()=>{ setRole(f.key); setPage(1); }}>{f.label}</Pill>
          ))}
        </div>
      </div>

      {/* ⚠ Kimondott magyarázat a darabszámhoz (K2 melléklete): a szám azt mondja meg, hány
          bizonylat VAN A PARTNERHEZ KÖTVE — nem azt, hány érkezett tőle. A kettő addig tér
          el, amíg egy bizonylat partnere nem oldható fel egyértelműen. Néma alulszámolás
          helyett kiírjuk. */}
      <p style={{fontSize:'0.7rem',color:'var(--text-muted)',marginBottom:'0.7rem'}}>
        A darabszám a partnerhez <b>kötött</b>, nem törölt bizonylatokat számolja.
        {role === 'all' && <> A <b>Mind</b> nézet a munkavállalókat nem tartalmazza (név és
        bérösszeg együtt személyes adat) — a saját szűrőjükön érhetők el.</>}
      </p>

      <Card noPad>
        <Table
          columns={columns} rows={rows} rowKey={p => p.id}
          sort={sort} onSort={handleSort}
          query={query} emptyText={q || role !== 'all'
            ? 'Nincs a szűrésnek megfelelő partner.'
            : 'Még nincs partner. A törzs a bizonylatokból épül.'}
          errorText="A partnerek betöltése sikertelen."
          ariaLabel="Partnerek" onRowClick={openPartner}
          rowAriaLabel={p => `${p.name} adatlapja`}
          isRowSelected={p => String(p.id) === String(pid)}
          renderRow={(p) => (
            <>
              <Td accent={String(p.id) === String(pid)}>
                <div style={{fontWeight:500,maxWidth:280,overflow:'hidden',
                  textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                {partnerIdent(p) && (
                  <div style={{fontSize:'0.68rem',color:'var(--text-muted)',fontFamily:'var(--mono)'}}>
                    {partnerIdent(p)}
                  </div>
                )}
              </Td>
              <Td><div style={{display:'flex',gap:4,flexWrap:'wrap'}}>{roleBadges(p)}</div></Td>
              <Td align="right" mono>{p.doc_counts?.total ?? 0}</Td>
              <Td align="right" mono>{p.doc_counts?.total_year ?? 0}</Td>
              <Td align="right" mono muted>{fmtDate(p.doc_counts?.last_document)}</Td>
            </>
          )}
          renderCard={(p) => (
            <ListCard
              title={p.name} subtitle={partnerIdent(p)} badges={roleBadges(p)}
              onClick={()=>openPartner(p)} selected={String(p.id) === String(pid)}
              titleRight={<span style={{fontFamily:'var(--mono)',fontSize:'0.85rem',fontWeight:600}}>
                {p.doc_counts?.total ?? 0}
              </span>}
              fields={[
                { label: `${new Date().getFullYear()}`, value: p.doc_counts?.total_year ?? 0, mono: true },
                { label: 'Utolsó', value: fmtDate(p.doc_counts?.last_document), mono: true },
              ]}/>
          )}/>
      </Card>

      {totalPages > 1 && (
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
          <Pagination mode="count" page={safePage} totalPages={totalPages} onPageChange={setPage}/>
        </div>
      )}

      {pid && <PartnerPanel pid={pid} onClose={()=>nav('/partners')}/>}
    </div>
  );
}

/* ── Részletpanel ─────────────────────────────────────────────────────────── */

const secS = { padding:'0.75rem 1.1rem', borderBottom:'1px solid var(--border-dim)' };
const secH = { fontSize:'0.64rem', fontWeight:700, letterSpacing:'0.09em',
  textTransform:'uppercase', color:'var(--text-muted)', marginBottom:8 };

function KV({ label, value, mono = true }) {
  const empty = value === null || value === undefined || value === '' || value === '–';
  return (
    <div style={{display:'flex',justifyContent:'space-between',gap:12,padding:'2.5px 0',
      fontSize:'0.8rem',alignItems:'baseline'}}>
      <span style={{color:'var(--text-muted)',flexShrink:0}}>{label}</span>
      <span style={{fontFamily:mono?'var(--mono)':undefined,textAlign:'right',
        fontVariantNumeric:'tabular-nums',overflowWrap:'anywhere'}}>
        {empty ? <span style={{color:'var(--text-dim)'}}>–</span> : value}
      </span>
    </div>
  );
}

function PartnerPanel({ pid, onClose }) {
  const query = useQuery({
    queryKey: ['partner', pid],
    queryFn: () => partnersApi.get(pid),
    enabled: !!pid,
  });
  const p = query.data;
  const c = p?.doc_counts || {};

  return (
    <SlideOver open onClose={onClose} width={560} ariaLabel={p?.name || 'Partner'}>
      <div style={{padding:'0.85rem 1.1rem',borderBottom:'1px solid var(--border)'}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10}}>
          <div style={{minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:'0.95rem',fontWeight:700}}>{p?.name || 'Partner'}</span>
              {(p?.roles || []).map(r => (
                <Badge key={r} variant={ROLE_BADGE[r] || 'default'}>{ROLE_LABEL[r]}</Badge>
              ))}
              {p?.is_recurring_vendor && <Badge variant="default">Rendszeres szállító</Badge>}
            </div>
            <div style={{fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-muted)',marginTop:2}}>
              {p ? (partnerIdent(p) || '–') : '–'}
            </div>
          </div>
          <button onClick={onClose} aria-label="Bezárás"
            style={{background:'none',border:'none',color:'var(--text-muted)',
              cursor:'pointer',lineHeight:1,flexShrink:0,padding:2}}>
            <X size={16}/>
          </button>
        </div>
      </div>

      <div style={{overflowY:'auto',flex:1}}>
        <QueryState query={query} errorText="A partner betöltése sikertelen.">
          {() => (
            <>
              <div style={secS}>
                <div style={secH}>Törzsadat</div>
                {p.partner_kind === 'authority'
                  ? <KV label="Hatósági számlaszám" value={fmtIban(p.authority_account_iban)}/>
                  : <KV label="Adószám" value={p.tax_number}/>}
                {p.foreign_vat_number && <KV label="Külföldi VAT" value={p.foreign_vat_number}/>}
                <KV label="Cím" value={p.address} mono={false}/>
                <KV label="Alapértelmezett kategória" value={p.default_category} mono={false}/>
                {p.portal_url && <KV label="Portál" value={p.portal_url} mono={false}/>}
                {/* D26 (2026-09-27): munkavállaló-partnernél nem-ADMIN-nak a bér-bizonylat
                    darabszámként sem jelenik meg — a címke kimondja (U2, INV-35). */}
                <KV label={p.pay_fields_hidden ? 'Bizonylat összesen (bér nélkül)' : 'Bizonylat összesen'}
                  value={c.total ?? 0}/>
                <KV label={`Ebben az évben (${new Date().getFullYear()})`} value={c.total_year ?? 0}/>
                <KV label="Ebből bejövő / kimenő" value={`${c.incoming ?? 0} / ${c.outgoing ?? 0}`}/>
              </div>

              <div style={secS}>
                <div style={secH}>Bankszámlák</div>
                {/* ⚠ K8: ezek KIZÁRÓLAG a bizonylatokon LÁTOTT számlaszámok — kézzel nem
                    lehet ide írni, és a `seen_count`/`first_seen` miatt egy IBAN-váltás
                    dátumozható marad (nem némán felülírt érték). */}
                {p.pay_fields_hidden ? (
                  <div style={{fontSize:'0.78rem',color:'var(--text-dim)'}}>
                    A munkavállaló bankszámlaszámát csak cég-adminisztrátor látja.
                  </div>
                ) : (p.bank_accounts || []).length === 0 ? (
                  <div style={{fontSize:'0.78rem',color:'var(--text-dim)'}}>
                    Nincs megfigyelt bankszámla.
                  </div>
                ) : p.bank_accounts.map(a => (
                  <div key={a.id} style={{display:'flex',justifyContent:'space-between',
                    gap:10,padding:'3px 0',fontSize:'0.78rem',alignItems:'baseline'}}>
                    <span style={{fontFamily:'var(--mono)',overflowWrap:'anywhere'}}>
                      {fmtIban(a.iban)}
                      {a.is_primary && <Badge variant="success">elsődleges</Badge>}
                    </span>
                    <span style={{color:'var(--text-muted)',whiteSpace:'nowrap',flexShrink:0}}>
                      {a.currency} · {a.seen_count}× · {fmtDate(a.last_seen)}
                    </span>
                  </div>
                ))}
              </div>

              <div style={secS}>
                <div style={secH}>Kategória-szabály</div>
                {(p.category_rules || []).length === 0 ? (
                  <div style={{fontSize:'0.78rem',color:'var(--text-dim)'}}>
                    Nincs erre a partnerre illeszkedő szabály.
                  </div>
                ) : p.category_rules.map(r => (
                  <KV key={r.id} mono={false}
                    label={r.supplier_tax_root ? `adószám ${r.supplier_tax_root}` : r.pattern}
                    value={`${r.category} (prioritás ${r.priority})`}/>
                ))}
                <div style={{marginTop:8}}>
                  <Link to="/settings/categories" style={{fontSize:'0.75rem',color:'var(--primary)'}}>
                    Kategória-szabályok kezelése →
                  </Link>
                </div>
              </div>

              <div style={secS}>
                <div style={secH}>Legutóbbi bizonylatok</div>
                {(p.documents || []).length === 0 ? (
                  <div style={{fontSize:'0.78rem',color:'var(--text-dim)'}}>Nincs bizonylat.</div>
                ) : p.documents.map(d => {
                  const m = MATCH_LABEL[d.partner_match_source];
                  return (
                    <div key={d.id} style={{display:'flex',justifyContent:'space-between',gap:10,
                      padding:'4px 0',fontSize:'0.78rem',alignItems:'baseline'}}>
                      <span style={{minWidth:0}}>
                        <Link to={`/documents/${d.id}`} style={{color:'var(--text)'}}>
                          {d.invoice_number || `#${d.id}`}
                        </Link>
                        <span style={{color:'var(--text-muted)',marginLeft:6}}>{fmtDate(d.issue_date)}</span>
                        {/* proveniencia — a K9 feltétele: a névre illesztés LÁTSZIK */}
                        {m && <Badge variant={m.variant}>{m.text}</Badge>}
                      </span>
                      <span style={{flexShrink:0}}>
                        <Amount value={d.gross_amount} currency={d.currency || 'HUF'}/>
                      </span>
                    </div>
                  );
                })}
                {/* A teljes lista a Bizonylatok oldal PARTNER-szűrőjén (2026-09-16): a
                    `documents.partner_id`-re szűr, nem névre — tehát ugyanazt a halmazt
                    adja, mint a fenti darabszám. A Bizonylatok oldal ilyenkor MINDHÁROM
                    alapszűrőt kinyitja, különben egy vevő-partner számlái eltűnnének. */}
                {(p.doc_counts?.total ?? 0) > 0 && (
                  <div style={{marginTop:8}}>
                    <Link to={`/documents?partner_id=${p.id}`}
                      style={{fontSize:'0.75rem',color:'var(--primary)'}}>
                      Mind a(z) {p.doc_counts.total} bizonylat megnyitása →
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </QueryState>
      </div>
    </SlideOver>
  );
}
